import { getSrpPathPage, SRP_PAGE_SEGMENT } from '@/src/lib/seo/srp-pagination';

/**
 * Promote-only canonical SRP URL: the path with its slug segment swapped to the
 * BE's `resolvedSlug`, keeping query + page + locale verbatim — or `null` when
 * already on the resolved slug.
 *
 * No param stripping: redundant params (e.g. `?pets=cats-dogs` on
 * `/pet-friendly-…`) are kept, like legacy. That keeps the client morph a single
 * navigation and means the server never issues a strip-redirect mid-nav (which
 * is what blanked the page). Pure, so the server route (load-time redirect) and
 * the client hook (`useSrpSlugCanonicalization`) share it.
 */
export function computeCanonicalSrpUrl(args: {
  pathname: string;
  search: string;
  locales: ReadonlyArray<string>;
  resolvedSlug: string | undefined;
}): string | null {
  const { pathname, search, locales, resolvedSlug } = args;
  if (!resolvedSlug) return null;

  const segments = pathname.split('/').filter(Boolean);
  const hasLocale = (locales as ReadonlyArray<string>).includes(segments[0] ?? '');
  const localePrefix = hasLocale ? `/${segments[0]}` : '';
  const route = hasLocale ? segments.slice(1) : segments;
  // Only the 2-segment area SRP shape morphs. Strip the page tail before
  // reading segments so a paginated near-me path (/{slug}/page/N) isn't
  // misread as {resourceId: slug, slug: 'page'}; near-me URLs (1 base
  // segment) own their canonical and never morph here.
  const page = getSrpPathPage(pathname);
  const base = page !== null ? route.slice(0, -2) : route;
  const [resourceId, currentSlug] = base;
  if (base.length !== 2 || !resourceId || !currentSlug) return null;
  if (currentSlug === resolvedSlug) return null; // already canonical

  const pageTail = page !== null ? `/${SRP_PAGE_SEGMENT}/${page}` : '';
  const normalizedSearch = search && !search.startsWith('?') ? `?${search}` : search;
  return `${localePrefix}/${resourceId}/${resolvedSlug}${pageTail}${normalizedSearch}`;
}
